/**
 * ConnectionNetwork — Northstar Stage 3 → Stage 4.
 *
 * Ten radial connection paths, one per emblem. Idle: static, subtle
 * gradient stroke terminating at R2 with a small luminous end-node.
 *
 * Stage 4 addition: when the user hovers, focuses, or activates one
 * emblem, ONLY that pathway responds. The line brightens, the end
 * node gains a soft luminous ring, and (unless reduced-motion is
 * requested) a slow pulse cycles once every 2.4 s along the stroke.
 * All other pathways remain at idle. The design principle is
 * "Northstar responds to attention, not demands it."
 */
import React from "react";
import { ORBS } from "../../../constants/northstarOrbs";

const RING_SCALE_R2 = 2.29;   // matches DigitalRingSystem
const RING_SCALE_R5 = 5.14;   // for SVG canvas sizing parity

export default function ConnectionNetwork({
  identity = 260,
  orbSize = 84,
  columnX = 380,
  rowYs = [-300, -150, 0, 150, 300],
  tier = "desktop-lg",
  hoveredKey = null,
  reducedMotion = false,
}) {
  if (tier === "narrow" || tier === "mobile") return null;

  const photoR = identity / 2;
  const R2 = photoR * RING_SCALE_R2;
  const emblemR = orbSize / 2;

  const outerR = photoR * RING_SCALE_R5 + 8;
  const size = outerR * 2 + 40;
  const cx = size / 2;
  const cy = size / 2;

  return (
    <svg
      aria-hidden="true"
      data-testid="northstar-connection-network"
      data-tier={tier}
      className="absolute left-1/2 top-1/2 pointer-events-none"
      style={{ transform: "translate(-50%, -50%)", zIndex: 4 }}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
    >
      <defs>
        {ORBS.map((orb, i) => {
          const g = geometryFor(i, columnX, rowYs, emblemR, R2, cx, cy);
          return (
            <React.Fragment key={orb.key}>
              <linearGradient
                id={`ns-conn-${orb.key}`}
                gradientUnits="userSpaceOnUse"
                x1={g.startX}
                y1={g.startY}
                x2={g.endX}
                y2={g.endY}
              >
                <stop offset="0%"   stopColor="rgba(184,239,198,0.78)" />
                <stop offset="55%"  stopColor="rgba(184,239,198,0.36)" />
                <stop offset="100%" stopColor="rgba(184,239,198,0.14)" />
              </linearGradient>
              <linearGradient
                id={`ns-conn-active-${orb.key}`}
                gradientUnits="userSpaceOnUse"
                x1={g.startX}
                y1={g.startY}
                x2={g.endX}
                y2={g.endY}
              >
                <stop offset="0%"   stopColor="rgba(223,247,230,1)" />
                <stop offset="60%"  stopColor="rgba(184,239,198,0.72)" />
                <stop offset="100%" stopColor="rgba(126,212,147,0.32)" />
              </linearGradient>
            </React.Fragment>
          );
        })}
      </defs>

      {ORBS.map((orb, i) => {
        const g = geometryFor(i, columnX, rowYs, emblemR, R2, cx, cy);
        const active = hoveredKey === orb.key;
        return (
          <g
            key={orb.key}
            data-connection-for={orb.key}
            data-active={active ? "true" : "false"}
            style={{
              transition: "opacity 260ms ease-out",
            }}
          >
            {/* Idle stroke — always drawn. */}
            <path
              d={g.path}
              fill="none"
              stroke={`url(#ns-conn-${orb.key})`}
              strokeWidth={0.75}
              strokeLinecap="round"
              style={{
                opacity: active ? 0 : 1,
                transition: "opacity 220ms ease-out",
              }}
            />
            {/* Active stroke — brighter, thicker, only rendered when
                active. Fades in on hover; fades out on release. */}
            <path
              d={g.path}
              fill="none"
              stroke={`url(#ns-conn-active-${orb.key})`}
              strokeWidth={1.5}
              strokeLinecap="round"
              style={{
                opacity: active ? 1 : 0,
                transition: "opacity 260ms ease-out",
                filter: active ? "drop-shadow(0 0 3px rgba(126,212,147,0.55))" : undefined,
              }}
            />
            {/* End node — diffuse halo + core dot. The halo expands
                and brightens when the pathway is active. */}
            <circle
              cx={g.endX}
              cy={g.endY}
              r={active ? 6.5 : 4.2}
              fill={
                active
                  ? "rgba(184,239,198,0.28)"
                  : "rgba(184,239,198,0.14)"
              }
              style={{ transition: "r 260ms ease-out, fill 260ms ease-out" }}
            />
            <circle
              cx={g.endX}
              cy={g.endY}
              r={active ? 2.6 : 2.0}
              fill={
                active
                  ? "rgba(223,247,230,1)"
                  : "rgba(184,239,198,0.85)"
              }
              style={{ transition: "r 220ms ease-out, fill 260ms ease-out" }}
            />
            {/* Pulse — a slow travelling highlight along the active
                stroke. Reduced-motion skips this entirely. */}
            {active && !reducedMotion && (
              <path
                d={g.path}
                fill="none"
                stroke="rgba(223,247,230,0.95)"
                strokeWidth={1.4}
                strokeLinecap="round"
                strokeDasharray="6 100"
                style={{
                  animation: `northstar-conn-pulse-${i} 2400ms ease-in-out infinite`,
                }}
              />
            )}
          </g>
        );
      })}

      {/* Keyframes — one per pathway so dashOffset animates from
          start (near emblem) to end (at R2). */}
      <style>{ORBS.map((orb, i) => {
        const g = geometryFor(i, columnX, rowYs, emblemR, R2, cx, cy);
        // Approx path length using straight-line distance × 1.05 for the bow.
        const len = Math.hypot(g.endX - g.startX, g.endY - g.startY) * 1.05;
        return `@keyframes northstar-conn-pulse-${i} {
          0%   { stroke-dashoffset: ${len}; opacity: 0; }
          10%  { opacity: 0.9; }
          80%  { opacity: 0.9; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }`;
      }).join("\n")}</style>
    </svg>
  );
}

function geometryFor(i, columnX, rowYs, emblemR, R2, cx, cy) {
  const isLeft = i < 5;
  const rowIndex = isLeft ? i : i - 5;
  const y = rowYs[rowIndex];
  const x = isLeft ? -columnX : columnX;

  const vLen = Math.hypot(x, y) || 1;
  const ux = x / vLen;
  const uy = y / vLen;

  const startX = cx + x - ux * (emblemR + 3);
  const startY = cy + y - uy * (emblemR + 3);

  const endX = cx + ux * R2;
  const endY = cy + uy * R2;

  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  const bow = 10;
  const perpX = -uy;
  const perpY = ux;
  const sign = isLeft ? -1 : 1;
  const cpX = midX + perpX * bow * sign;
  const cpY = midY + perpY * bow * sign;

  const path = `M ${startX} ${startY} Q ${cpX} ${cpY} ${endX} ${endY}`;
  return { path, startX, startY, endX, endY };
}
